import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ExternalLink, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ApprovalPage() {
  const { approvalId } = useParams();
  const [approval, setApproval] = useState(null);
  const [eventHistory, setEventHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchApprovalData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/proof/proof-approvals/${approvalId}`);
      setApproval(response.data.approval);
      setEventHistory(response.data.event_history);
    } catch (error) {
      toast.error("Failed to load approval request");
    } finally {
      setLoading(false);
    }
  }, [approvalId]);

  useEffect(() => {
    fetchApprovalData();
  }, [fetchApprovalData]);

  const handleResponse = async (status) => {
    // Validate comment is provided
    if (!comment || comment.trim() === '') {
      toast.error('Please add a comment before submitting your response');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${status} this proof?`)) return;

    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/api/proof/proof-approvals/${approvalId}/respond`, {
        status,
        customer_comment: comment.trim()
      });
      toast.success(`Proof ${status} successfully!`);
      setSubmitted(true);
      await fetchApprovalData(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} proof`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Approval Not Found</h1>
          <p className="text-gray-600 mt-2">This approval link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  const isPending = approval.status === "pending";
  const isApproved = approval.status === "approved";
  const isRejected = approval.status === "rejected";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8 gap-3">
          <Logo size={48} showWordmark={false} />
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Print Proof Approval</h1>
          <p className="text-gray-600">analyticaY Print Shop</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
          {/* Status Badge */}
          {!isPending && (
            <div className={`mb-6 p-4 rounded-lg text-center ${isApproved ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'}`}>
              <div className="flex items-center justify-center gap-2">
                {isApproved ? (
                  <>
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span className="text-xl font-bold text-green-600">APPROVED</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-6 w-6 text-red-600" />
                    <span className="text-xl font-bold text-red-600">REJECTED</span>
                  </>
                )}
              </div>
              {approval.submitted_at && (
                <p className="text-sm text-gray-600 mt-2">
                  {new Date(approval.submitted_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Job Details */}
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Job Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-700">Customer:</span>
                <span className="text-gray-900">{approval.customer_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-700">Event Code:</span>
                <span className="text-gray-900 font-mono">{approval.event_code}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-700">Artwork:</span>
                <span className="text-gray-900">{approval.artwork_name}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {approval.notes && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Notes from Print Shop:</h3>
              <p className="text-blue-800">{approval.notes}</p>
            </div>
          )}

          {/* Proof Link */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">View Proof:</h3>
            <a
              href={approval.google_drive_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              <ExternalLink className="h-5 w-5" />
              Open Proof in Google Drive
            </a>
          </div>

          {/* Customer Comment (if already submitted) */}
          {!isPending && approval.customer_comment && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Your Comment:</h3>
              <p className="text-gray-700">{approval.customer_comment}</p>
            </div>
          )}

          {/* Response Form (only if pending) */}
          {isPending && !submitted && (
            <>
              <div className="mb-6">
                <label className="block font-semibold text-gray-900 mb-2">
                  Comments (Required) *
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Please add your comments or feedback about the proof... (Required)"
                  className="w-full min-h-[100px] border-2"
                  data-testid="customer-comment-input"
                  required
                />
                <p className="text-sm text-gray-600 mt-2">* Comment is required to approve or reject</p>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={() => handleResponse("approved")}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                  data-testid="approve-button"
                >
                  <CheckCircle2 className="h-6 w-6 mr-2" />
                  {submitting ? "Submitting..." : "Approve"}
                </Button>
                <Button
                  onClick={() => handleResponse("rejected")}
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold"
                  data-testid="reject-button"
                >
                  <XCircle className="h-6 w-6 mr-2" />
                  {submitting ? "Submitting..." : "Reject"}
                </Button>
              </div>
            </>
          )}

          {submitted && (
            <div className="text-center p-6 bg-green-50 rounded-lg border-2 border-green-500">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-900 mb-2">Thank You!</h3>
              <p className="text-green-800">Your response has been submitted successfully.</p>
              <p className="text-green-700 mt-2">The production team has been notified.</p>
            </div>
          )}
        </div>

        {/* Approval History */}
        {eventHistory.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Approval History</h2>
            <div className="space-y-3">
              {eventHistory.map((item) => (
                <div key={item.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-shrink-0">
                    {item.status === "approved" ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : item.status === "rejected" ? (
                      <XCircle className="h-6 w-6 text-red-600" />
                    ) : (
                      <Clock className="h-6 w-6 text-yellow-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.artwork_name}</p>
                    <p className="text-sm text-gray-600">{item.event_code}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${item.status === 'approved' ? 'text-green-600' : item.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {item.status.toUpperCase()}
                    </p>
                    {item.submitted_at && (
                      <p className="text-xs text-gray-500">
                        {new Date(item.submitted_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
